import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/useProfile";
import { NavbarGibletChatBar } from "./chat/NavbarGibletChatBar";
import { ProfileDrawer } from "./navbar/ProfileDrawer";
import { useProfileDrawer } from "@/contexts/ProfileDrawerContext";
import { useAuth } from "@/contexts/AuthContext";


const Navbar = ({ children }: {children?: React.ReactNode;}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isLoading } = useProfile();
  const { user, isAuthenticated } = useAuth();
  const { openProfileDrawer } = useProfileDrawer();
  const isCreatorsPage = location.pathname === "/creators";
  const isIntroPage = location.pathname === "/intro";

  const handleBackClick = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  // Simplified navbar for intro page
  if (isIntroPage) {
    return (
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <img 
                src="/ispai-logo.png" 
                alt="iSPAI" 
                className="h-8 w-auto"
              />
            </div>
            <div className="flex items-center">
              <h1 className="text-lg font-medium">Integrated Special Purpose AI (iSPAI)</h1>
            </div>
          </div>
        </div>
      </nav>);

  }

  // Standard navbar for other pages
  return (
    <>
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side: Back, Logo */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={handleBackClick}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Go back">

                <ArrowLeft className="h-5 w-5" />
              </button>
              
              <button 
                onClick={() => navigate(isAuthenticated ? '/' : '/auth')}
                className="flex items-center"
              >
                <img 
                  src="/ispai-logo.png" 
                  alt="iSPAI" 
                  className="h-10 w-auto"
                />
              </button>

            </div>
            
            {/* Center: Giblet Chat Bar or children */}
            {!isCreatorsPage ?
            <NavbarGibletChatBar /> :
            children ?
            <div className="flex-1 flex justify-center">
                {children}
              </div> :

            <div className="flex-1" />
            }

            {/* Right side: Profile Avatar */}
            <div className="flex items-center flex-shrink-0 w-24 justify-end">
              <Tooltip>
                <TooltipTrigger asChild>
                  {isLoading ?
                  <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarFallback className="text-xs">...</AvatarFallback>
                    </Avatar> :
                  profile?.username ?
                  <button
                    onClick={() => user?.id && openProfileDrawer(user.id)}
                    className="text-black hover:text-black/80 transition-colors"
                    aria-label="Open Profile Menu">

                      <Avatar className="h-9 w-9 transform transition-transform duration-200 hover:scale-110" key={profile.avatar_url}>
                        <AvatarImage
                        src={profile.avatar_url || undefined}
                        alt={`mySPAI_${profile.display_name}`}
                        className="object-cover" />

                        <AvatarFallback className="text-xs font-medium">
                          {profile.display_name ? profile.display_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() : profile.email?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </button> :

                  <Link to="/create-myspai-profile" className="text-black hover:text-black/80 transition-colors" aria-label="Create Profile">
                      <Avatar className="h-9 w-9 transform transition-transform duration-200 hover:scale-110">
                        <AvatarFallback className="text-xs">+</AvatarFallback>
                      </Avatar>
                    </Link>
                  }
                </TooltipTrigger>
                <TooltipContent>{profile?.username ? `mySPAI_${profile.display_name || 'User'}` : 'Create Profile'}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </nav>
      
      <ProfileDrawer />
    </>);

};

export default Navbar;